import { useEffect, useRef, useState, useCallback } from 'react';
import * as pbi from 'powerbi-client';
import {
  getReportEmbed, getPaginatedEmbed, getDashboardEmbed,
  getVisualSettings, saveVisualSettings
} from '../api/client.js';
import { currentPeriodState } from '../lib/slicerDefaults.js';

const service = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

const FETCHERS = {
  report: getReportEmbed,
  paginated: getPaginatedEmbed,
  dashboard: getDashboardEmbed
};

/**
 * Embeds a Power BI report and manages its lifecycle.
 *
 * Improvements over the old dashClient.js:
 *  - the container is reset and the embed released on unmount, instead of
 *    leaving orphaned iframes behind on every nav click
 *  - embed tokens expire in ~an hour; this refreshes before expiry rather
 *    than letting the report silently go stale
 *  - saved slicer state is applied after the report loads, and restore
 *    failures do not take the whole view down
 */
export function usePowerBiEmbed({ kind, id, containerRef, onLoaded }) {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const embedRef = useRef(null);
  const refreshTimer = useRef(null);

  /**
   * What the report should be showing the moment it appears.
   *
   * Two mechanisms, and the precedence between them is the whole design:
   *
   *   1. A SAVED VIEW, if the user has one. It wins outright.
   *   2. Otherwise, month and year slicers open on the current period.
   *
   * A saved view suppresses (2) ENTIRELY - not slicer by slicer. That is
   * deliberate and it is not what v1 did.
   *
   * v1 decided per slicer: a saved value was used if present, and any other
   * month or year slicer was still reset. The flaw only shows up once saving
   * exists, which it did not when that code was written. `saveCurrentSlicers`
   * records slicers that HAVE a selection, so a slicer the user deliberately
   * cleared - "show me every month" - is absent from the saved view. Under
   * per-slicer precedence that absence is indistinguishable from "never
   * configured", so the cleared slicer snaps back to the current month on
   * every open and the one thing the user explicitly asked for is the one
   * thing that cannot be saved.
   *
   * Treating the saved view as a complete statement removes that: if the user
   * has said what this report should look like, the portal does not add to it.
   *
   * Failures are warned and swallowed. Every call here is against a report
   * that has already rendered, and a slicer that has since been deleted, or a
   * saved value that no longer exists in the model, must not replace a working
   * report with an error.
   */
  const applySlicerState = useCallback(async (report, reportId) => {
    try {
      const saved = await getVisualSettings(reportId);
      const pages = await report.getPages();

      if (saved.length) {
        for (const state of saved) {
          const page = pages.find(p => p.name === state.pageId);
          if (!page) continue;
          const visuals = await page.getVisuals();
          const slicer = visuals.find(v => v.name === state.slicerId && v.type === 'slicer');
          if (!slicer || !state.table || !state.column) continue;

          await slicer.setSlicerState({
            filters: [{
              $schema: 'http://powerbi.com/product/schema#basic',
              target: { table: state.table, column: state.column },
              operator: 'In',
              values: Array.isArray(state.value) ? state.value : [state.value]
            }]
          });
        }
        return;
      }

      /* The active page only, which is v1's scope.
         Every page would mean getVisuals() against every page of every report
         on every open, to set slicers on pages most users never reach - and
         Power BI syncs slicers across pages when the author asks it to, which
         covers the case that actually matters. */
      const active = pages.find(p => p.isActive) ?? pages[0];
      if (!active) return;

      const visuals = await active.getVisuals();
      for (const visual of visuals) {
        if (visual.type !== 'slicer') continue;
        const state = await visual.getSlicerState();
        const next = currentPeriodState(state);

        /* Why a slicer was or was not moved, at debug level.
           -------------------------------------------------------------------
           Chrome hides console.debug unless the level is set to Verbose, so
           this costs a normal user nothing and is there when somebody asks
           "why did this report not open on this month?".

           It exists because that question has no other answer from outside.
           A skipped slicer looks identical to a broken feature: the report
           opens on the wrong period either way. The three reasons are not
           distinguishable without seeing the state -

             - the slicer has no selection, so there is nothing to replace
             - the column is not called month or year
             - the value is in a shape we will not guess at (a fiscal year, a
               hierarchy slicer, a date encoding we do not recognise)

           and the state is also exactly what is needed to add support for a
           shape we do not handle yet. Logged whole rather than summarised for
           that reason: a summary would be written by whoever already believes
           they know what is in there. */
        if (!next) {
          console.debug(
            `Slicer "${visual.name}" left as it is. targets=`,
            state?.targets,
            'filter=', state?.filters?.[0]);
        }

        if (next) await visual.setSlicerState(next);
      }
    } catch (err) {
      // A slicer that no longer exists must not break the report.
      console.warn('Could not apply slicer state', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const config = await FETCHERS[kind](id);
        if (cancelled) return;

        service.reset(container);

        const isMobilePortrait =
          window.innerWidth < 768 && window.innerHeight > window.innerWidth;

        /* A paginated report is an RDL, and it does not take a report's
           configuration.
           -------------------------------------------------------------------
           `type` is 'report' for both - that part was right. What is not
           interchangeable is everything else. filterPaneEnabled,
           navContentPaneEnabled and layoutType are report-only; RDL has none of
           those surfaces. `permissions` is likewise meaningless for something
           that cannot be edited.

           Sent anyway, the SDK accepted the configuration and then signalled
           nothing at all - no `loaded`, no `error`, an empty console and a
           spinner that never resolved. A rejection would have been kinder.

           Note the nesting: parameterPanel sits under `commands`. v1 sends it
           flat, and that is not a difference to copy - v1 runs SDK 2.22.3 and
           this is 2.24.1, where IPaginatedReportSettings puts the panel under
           commands. Microsoft's example:

             settings: { commands: { parameterPanel: { enabled, expanded } } }

           https://learn.microsoft.com/javascript/api/overview/powerbi/embed-paginated-report */
        const paginated = kind === 'paginated';

        const embed = service.embed(container, {
          type: kind === 'dashboard' ? 'dashboard' : 'report',
          id: config.reportId ?? config.dashboardId,
          embedUrl: config.embedUrl,
          accessToken: config.token,
          tokenType: pbi.models.TokenType.Embed,
          ...(paginated ? {} : { permissions: pbi.models.Permissions.Read }),
          settings: paginated
            ? { commands: { parameterPanel: { enabled: true, expanded: true } } }
            : {
              filterPaneEnabled: false,
              navContentPaneEnabled: kind === 'report',
              layoutType: isMobilePortrait ? pbi.models.LayoutType.MobilePortrait : undefined
            }
        });

        embedRef.current = embed;

        /* Power BI raises `error` for two very different things, and this
           treated them alike.
           -------------------------------------------------------------------
           Some are fatal: the token was refused, the report does not exist,
           the configuration is wrong. Nothing rendered and nothing will.

           Others are raised by the report renderer AFTER the report is up, per
           visual - `Missing_References` when a visual points at a field the
           model no longer has, or a gateway being unreachable for one
           visual's query. Power BI's own service draws the rest of the report
           and puts an error box in that one visual.

           This handler set status='error' for both, so a single broken visual
           replaced the entire report with a red alert. v1 subscribed to no
           error event at all, which is why the same report renders there -
           that is not v1 being lax, it is this being wrong.

           `loaded` is the discriminator. If the report loaded, it is on the
           screen and the user can see for themselves which visual failed;
           blanking it takes away the working nine tenths and tells them less. */
        let loadedOnce = false;

        embed.off('loaded');
        embed.on('loaded', async () => {
          if (cancelled) return;
          loadedOnce = true;
          setStatus('ready');
          if (kind === 'report') await applySlicerState(embed, config.reportId);
          onLoaded?.(config);
        });

        embed.off('error');
        embed.on('error', (event) => {
          const detail = event.detail ?? {};

          if (loadedOnce) {
            /* Content, not embedding. Logged at warn so it is still findable -
               these are real problems for whoever owns the report - but the
               report stays on screen. */
            console.warn(
              'Power BI reported a problem inside a report that has already '
              + 'loaded. The report is still shown; this is usually a visual '
              + 'referencing a field the model no longer has, or a datasource '
              + 'it cannot reach.', detail);
            return;
          }

          console.error('Power BI embed error', detail);
          if (!cancelled) { setError(detail); setStatus('error'); }
        });

        /* Keep the token fresh for as long as the report is open.
           -------------------------------------------------------------------
           This scheduled ONE refresh and stopped. An embed token lasts about an
           hour, so a report left open on a wall display or a second monitor
           worked for two hours and then quietly stopped updating - no error,
           no prompt, just stale figures, which is the worst way for a reporting
           tool to fail.

           Each refresh now schedules the next from the NEW token's expiry
           rather than the original's. A failure retries in a minute instead of
           giving up, because the common causes - a blip, a brief 502 from
           Power BI - are transient, and the alternative is silence. */
        const scheduleRefresh = (expiresUtc) => {
          if (cancelled || !expiresUtc) return;

          const ms = new Date(expiresUtc).getTime() - Date.now() - 60_000;
          if (!Number.isFinite(ms)) return;

          clearTimeout(refreshTimer.current);
          refreshTimer.current = setTimeout(async () => {
            if (cancelled) return;
            try {
              const fresh = await FETCHERS[kind](id);
              if (cancelled) return;
              await embedRef.current?.setAccessToken(fresh.token);
              scheduleRefresh(fresh.expiresUtc);
            } catch (err) {
              console.warn('Embed token refresh failed; retrying in a minute', err);
              // Re-arm rather than stop. A token that cannot be refreshed will
              // expire and the report will fail visibly, which is the right
              // outcome - but only after we have actually run out of tries.
              if (!cancelled) {
                refreshTimer.current = setTimeout(
                  () => scheduleRefresh(new Date(Date.now() + 60_000).toISOString()), 60_000);
              }
            }
            // A token already inside the last minute of its life refreshes at
            // once rather than being skipped.
          }, Math.max(ms, 0));
        };

        scheduleRefresh(config.expiresUtc);
      } catch (err) {
        if (!cancelled) { setError(err); setStatus('error'); }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(refreshTimer.current);
      if (container) service.reset(container);
      embedRef.current = null;
    };
  }, [kind, id, containerRef, applySlicerState, onLoaded]);

  /** Persist the current slicer selections for this report. */
  const saveCurrentSlicers = useCallback(async () => {
    const report = embedRef.current;
    if (!report || kind !== 'report') return;

    const states = [];
    const pages = await report.getPages();
    for (const page of pages) {
      const visuals = await page.getVisuals();
      for (const visual of visuals.filter(v => v.type === 'slicer')) {
        const state = await visual.getSlicerState();
        const filter = state.filters?.[0];
        if (!filter?.target) continue;
        states.push({
          pageId: page.name,
          slicerId: visual.name,
          table: filter.target.table,
          column: filter.target.column,
          value: filter.values ?? []
        });
      }
    }
    await saveVisualSettings(id, states);
    return states.length;
  }, [id, kind]);

  const reload = useCallback(() => embedRef.current?.reload(), []);
  const fullscreen = useCallback(() => embedRef.current?.fullscreen(), []);
  const print = useCallback(() => embedRef.current?.print(), []);

  return { status, error, saveCurrentSlicers, reload, fullscreen, print };
}
